import React from "react";
import TreeDiagram from "./TreeDiagram";
import DetailPanel from "./DetailPanel";
import NodeTables from "./NodeTables";
import TraditionSwitch from "./TraditionSwitch";
import LensPicker from "./LensPicker";
import ViewToggle from "./ViewToggle";
import SequenceTransport from "./SequenceTransport";
import RegionCallouts from "./RegionCallouts";
import useTreeState from "./useTreeState";
import { groupColour } from "./lib/palette";
import { sequenceLegend } from "./OverlayLayer";

// WHAT THE TREE LOOKS LIKE. What it knows is in `useTreeState`.
//
// Nothing is worked out in this file: every value it reads is already settled,
// which is what stops a number being derived here and differently somewhere
// else. The paint order the diagram depends on is inside `TreeDiagram`; the
// order of the blocks below is the reading order of the page.
export default function TreeOfLife({ tree }) {
  const {
    availableTraditions, channelParams, multiChannels, overlays, paths, sephiroth,
    tradition, viewBox,
    rootRef, showNames, wide,
    handleSelect, selected, setSelected, selectedNode,
    active, activeCount, clearChannel, clearLenses, handleToggleLens,
    legendOverlays, lenses, regionOverlay, sequenceOverlay, shapeOverlays,
    inscription, inscriptionLens, setSphereInscription,
    goToStep, playing, sequenceState, setPlaying, setSpeed, speed, step, steps,
    aboutOpen, copied, copyLink, setAboutOpen, setSheetOpen, setView, sheetOpen, view,
    drawnSephiroth, pathsBySlug, scale, scaleSlug, scales, sephirothBySlug, setScale,
    setShowUncounted, showUncounted, uncounted
  } = useTreeState(tree);

  const picker = (
    <LensPicker
      overlays={overlays}
      tradition={tradition}
      lenses={lenses}
      channelParams={channelParams}
      multiChannels={multiChannels}
      onToggle={handleToggleLens}
      onClear={clearLenses}
      onClearChannel={clearChannel}
    />
  );

  return (
    <div className={wide ? "tree tree--wide" : "tree"} ref={rootRef}>
      <header className="tree__masthead">
        <div className="bar">
          <h1 className="tree__heading">Tree of Life</h1>
          <TraditionSwitch
            traditions={availableTraditions}
            current={tradition}
            selected={selected}
          />
          <ViewToggle view={view} onChange={setView} />
        </div>

        {/* The provenance is important AND it is reference material, which is
            what a disclosure is for. The subtitle used to repeat the tradition
            card verbatim; it lives on the card now. */}
        <details
          className="about"
          open={aboutOpen}
          onToggle={(event) => setAboutOpen(event.currentTarget.open)}
          data-testid="about"
        >
          <summary className="about__summary">About this tradition</summary>
          <p className="about__lead">{tradition.shortDescription}</p>
          <p className="about__provenance">{tradition.provenance}</p>
          {/* The chariot literature is six centuries older than any of this and
              is NOT drawn on the diagram, because whether it belongs there is
              the disputed question. A link is the honest amount of connection. */}
          {tradition.family === "jewish" && (
            <>
              <p className="about__more">
                <a href="/merkavah" data-testid="merkavah-link">
                  Before the sefirot: Merkavah and the Hekhalot literature →
                </a>
              </p>
              {tradition.slug === "jewish_kabbalah_gra" && (
                <p className="about__more">
                  <a href="/tree/gra-arrangement" data-testid="gra-research-link">
                    Read the sources and geometry of this arrangement →
                  </a>
                </p>
              )}
            </>
          )}
        </details>
      </header>

      {overlays.length > 0 && (
        <div className="tree__rail">
          {wide ? (
            picker
          ) : (
            <>
              <button
                type="button"
                className="lenses__opener"
                aria-expanded={sheetOpen}
                onClick={() => setSheetOpen((open) => !open)}
                data-testid="lens-sheet-toggle"
              >
                Lenses{activeCount > 0 ? ` (${activeCount})` : ""}
              </button>
              {sheetOpen && (
                <div className="lenses__sheet" data-testid="lens-sheet">
                  {picker}
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div
        className="tree__figure"
        id="view-panel"
        role="group"
        aria-label={view === "table" ? "Tree of Life, as a table" : "Tree of Life, as a diagram"}
      >
        {/* Announced, because the switch is visually obvious and silent
            otherwise. */}
        <p className="visually-hidden" aria-live="polite">
          {view === "tree" ? "Tree view" : "Table view"}
        </p>

        {view === "tree" && (
        <div className={regionOverlay ? "tree__diagram-layout tree__diagram-layout--regions" : "tree__diagram-layout"}>
          <TreeDiagram
            sephiroth={drawnSephiroth}
            paths={paths}
            viewBox={viewBox}
            scale={scale}
            selected={selected}
            onSelect={handleSelect}
            showLabels={showNames}
            showPathLabels={wide}
            shapeOverlays={shapeOverlays}
            sequenceOverlay={sequenceOverlay}
            steps={steps}
            step={step}
            sephirothBySlug={sephirothBySlug}
            pathsBySlug={pathsBySlug}
            sequenceState={sequenceState}
            sphereInscription={inscription}
          />
          <RegionCallouts
            overlay={regionOverlay}
            tradition={tradition}
            viewBox={viewBox}
            sephirothBySlug={sephirothBySlug}
            selected={selected}
            onSelect={setSelected}
          />
        </div>
        )}

        {view === "table" && (
          <NodeTables
            sephiroth={drawnSephiroth}
            paths={paths}
            selected={selected}
            onSelect={handleSelect}
            pathsNote={tradition.pathsNote}
            regionOverlay={regionOverlay}
            sequence={
              sequenceOverlay && steps.length
                ? {
                    name: sequenceOverlay.name,
                    kind: steps[0].kind,
                    steps
                  }
                : null
            }
          />
        )}

        {view === "tree" && sequenceOverlay && (
          <SequenceTransport
            overlay={sequenceOverlay}
            steps={steps}
            step={step}
            playing={playing}
            fixed={!wide}
            speed={speed}
            onSpeed={setSpeed}
            onStep={goToStep}
            onTogglePlay={setPlaying}
          />
        )}

        <div className="tree__controls">
          <fieldset
            className="scale sphere-inscription"
            disabled={Boolean(inscriptionLens)}
            data-testid="sphere-inscription"
          >
            <legend className="scale__legend">Sphere inscription</legend>
            <label className="scale__option">
              <input
                type="radio"
                name="sphere-inscription"
                checked={inscription === "number"}
                onChange={() => setSphereInscription("number")}
              />
              <span>Number</span>
            </label>
            {sephiroth.some((sphere) => sphere.hebrew) && (
              <label className="scale__option">
                <input
                  type="radio"
                  name="sphere-inscription"
                  checked={inscription === "hebrew"}
                  onChange={() => setSphereInscription("hebrew")}
                  data-testid="hebrew-name-toggle"
                />
                <span>Hebrew name</span>
              </label>
            )}
            {sephiroth.some((sphere) => sphere.planetSymbol) && (
              <label className="scale__option">
                <input
                  type="radio"
                  name="sphere-inscription"
                  checked={inscription === "planet"}
                  onChange={() => setSphereInscription("planet")}
                  data-testid="planet-symbol-toggle"
                />
                <span>Planet symbol</span>
              </label>
            )}
            {inscriptionLens && (
              <p className="scale__note" data-testid="inscription-driven-by-lens">
                Set by the {inscriptionLens.name} lens.
              </p>
            )}
          </fieldset>

          {uncounted.length > 0 && (
            <fieldset className="scale">
              <legend className="scale__legend">Drawn, not counted</legend>
              <label className="scale__option">
                <input
                  type="checkbox"
                  checked={showUncounted}
                  onChange={(event) => setShowUncounted(event.target.checked)}
                  data-testid="uncounted-toggle"
                />
                <span>{uncounted.map((node) => node.name).join(", ")}</span>
              </label>
              <p className="scale__note">
                {uncounted.length === 1 ? "This node is" : "These nodes are"} drawn on
                the Tree without being counted among the ten. Hiding{" "}
                {uncounted.length === 1 ? "it" : "them"} leaves the ten.
              </p>
            </fieldset>
          )}
          {/* ONE CONTROL FOR BOTH, because the Minutum Mundum cannot be said
              any other way: it is the Queen column on the spheres AND the King
              column on the paths, which is the pairing almost every printed
              Tree uses and the one this diagram opened on, unnamed, for as long
              as it has existed. Splitting it back into a sphere control and a
              path control would make the default unsayable and leave the paths
              nailed to whatever the tradition declared.

              The note carries the world rather than the diagram carrying it:
              the scales ARE the four worlds, and a reader who does not know
              that is looking at five arbitrary palettes. */}
          {scales.length > 1 && (
            <fieldset className="scale" data-testid="colour-scale">
              <legend className="scale__legend">Colour scale</legend>
              {scales.map((option) => (
                <label key={option.slug} className="scale__option">
                  <input
                    type="radio"
                    name="scale"
                    value={option.slug}
                    checked={scaleSlug === option.slug}
                    onChange={() => setScale(option.slug)}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
              <p className="scale__note" data-testid="colour-scale-note">
                {scale.world ? (
                  <>
                    <strong>{scale.world}.</strong>{" "}
                  </>
                ) : null}
                {scale.summary}{" "}
                {tradition.colorScalesPath && (
                  <a href={tradition.colorScalesPath}>What the four scales are</a>
                )}
              </p>
            </fieldset>
          )}

          <button type="button" className="share" onClick={copyLink} data-testid="copy-link">
            {copied ? "Link copied" : "Copy link to this view"}
          </button>
        </div>

        {/* The overlay's own text, under the diagram rather than in a tooltip.
            This is a study guide; the text is the point. */}
        {view === "tree" && sequenceOverlay && (
          <p className="seq-note" data-testid="sequence-note">
            <strong>{sequenceOverlay.name}.</strong> {sequenceOverlay.summary}{" "}
            {sequenceLegend(sequenceOverlay, steps)}
          </p>
        )}

        {view === "tree" && legendOverlays.length > 0 && (
          <div className="legend" data-testid="legend">
            {legendOverlays.map((overlay) => (
              <section className="legend__block" key={overlay.slug}>
                <h2 className="legend__heading">{overlay.name}</h2>
                <ul className="legend__list">
                  {overlay.groups.map((group) => (
                    <li className="legend__item" key={group.slug}>
                      <span
                        className="legend__swatch"
                        style={{ background: groupColour(group) }}
                        aria-hidden="true"
                      />
                      <span>
                        <span className="legend__name">{group.name}</span>
                        {group.note && <span className="legend__note">{group.note}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>

      <div className="tree__aside">
        <DetailPanel
          node={selectedNode}
          tradition={tradition}
          counts={{
            sephiroth: sephiroth.length,
            paths: paths.length,
            counted: sephiroth.filter((s) => s.countedInTen !== false).length
          }}
          lenses={active}
          sequence={
            sequenceOverlay
              ? {
                  overlay: sequenceOverlay,
                  step,
                  total: steps.length,
                  current: steps[step - 1]
                }
              : null
          }
        />
      </div>
    </div>
  );
}
